import React, { useState } from 'react';
import { useWhatsAppAccount } from '@/hooks/whatsapp';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, ExternalLink, Info, ArrowRight } from 'lucide-react';

interface WhatsAppSetupProps {
  businessId: string | null;
}

type SetupStep = 'intro' | 'meta-setup' | 'credentials' | 'webhook';

export function WhatsAppSetup({ businessId }: WhatsAppSetupProps) {
  const { connect, isLoading } = useWhatsAppAccount(businessId);
  const [step, setStep] = useState<SetupStep>('intro');
  const [credentials, setCredentials] = useState({
    waba_id: '',
    phone_number_id: '',
    access_token: '',
  });
  const [webhookInfo, setWebhookInfo] = useState<{ url: string; verify_token: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleConnect = async () => {
    if (!credentials.waba_id || !credentials.phone_number_id || !credentials.access_token) {
      setError('Please fill in all fields');
      return;
    }

    setError(null);
    try {
      await connect({
        app_id: '',
        app_secret: '',
        waba_id: credentials.waba_id,
        phone_number_id: credentials.phone_number_id,
        access_token: credentials.access_token,
      });
      // If successful, the component will re-render showing the chat interface
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection failed');
    }
  };

  if (step === 'intro') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-8">
        <Card className="max-w-2xl">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <svg className="h-10 w-10 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>
            <CardTitle className="text-2xl">Connect WhatsApp Business</CardTitle>
            <CardDescription className="text-base">
              Chat with your customers directly through WhatsApp. Receive messages, send replies,
              and use templates to start new conversations.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-lg border p-4 text-center">
                <div className="mb-2 text-2xl">1</div>
                <h4 className="font-medium">Meta Business Setup</h4>
                <p className="text-sm text-muted-foreground">Create a Meta Business account and WhatsApp app</p>
              </div>
              <div className="rounded-lg border p-4 text-center">
                <div className="mb-2 text-2xl">2</div>
                <h4 className="font-medium">Enter Credentials</h4>
                <p className="text-sm text-muted-foreground">Copy your API credentials from Meta</p>
              </div>
              <div className="rounded-lg border p-4 text-center">
                <div className="mb-2 text-2xl">3</div>
                <h4 className="font-medium">Configure Webhook</h4>
                <p className="text-sm text-muted-foreground">Set up webhook to receive messages</p>
              </div>
            </div>

            <Button onClick={() => setStep('meta-setup')} className="w-full" size="lg">
              Get Started <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'meta-setup') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-8">
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>Step 1: Set Up Meta Business</CardTitle>
            <CardDescription>
              Follow these steps to create your WhatsApp Business API account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex gap-4 rounded-lg border p-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 font-medium">
                  1
                </div>
                <div>
                  <h4 className="font-medium">Create a Meta Business Account</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Go to Meta Business Suite and create or select your business account.
                  </p>
                  <Button variant="outline" size="sm" asChild>
                    <a href="https://business.facebook.com/" target="_blank" rel="noopener noreferrer">
                      Open Meta Business Suite <ExternalLink className="ml-2 h-3 w-3" />
                    </a>
                  </Button>
                </div>
              </div>

              <div className="flex gap-4 rounded-lg border p-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 font-medium">
                  2
                </div>
                <div>
                  <h4 className="font-medium">Create a Meta App</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Go to Meta Developers and create a new app with WhatsApp product.
                  </p>
                  <Button variant="outline" size="sm" asChild>
                    <a href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer">
                      Open Meta Developers <ExternalLink className="ml-2 h-3 w-3" />
                    </a>
                  </Button>
                </div>
              </div>

              <div className="flex gap-4 rounded-lg border p-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 font-medium">
                  3
                </div>
                <div>
                  <h4 className="font-medium">Add WhatsApp Product</h4>
                  <p className="text-sm text-muted-foreground">
                    In your app, add the WhatsApp product and complete the setup wizard to get your
                    WhatsApp Business Account ID and Phone Number ID.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 rounded-lg border p-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 font-medium">
                  4
                </div>
                <div>
                  <h4 className="font-medium">Generate Access Token</h4>
                  <p className="text-sm text-muted-foreground">
                    In your app settings, generate a permanent access token with whatsapp_business_messaging permission.
                  </p>
                </div>
              </div>
            </div>

            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Make sure to copy your WhatsApp Business Account ID, Phone Number ID, and Access Token.
                You'll need them in the next step.
              </AlertDescription>
            </Alert>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep('intro')}>
                Back
              </Button>
              <Button onClick={() => setStep('credentials')} className="flex-1">
                I have my credentials <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'credentials') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-8">
        <Card className="max-w-lg w-full">
          <CardHeader>
            <CardTitle>Step 2: Enter Your Credentials</CardTitle>
            <CardDescription>
              Enter the credentials from your Meta WhatsApp app
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="waba_id">WhatsApp Business Account ID</Label>
              <Input
                id="waba_id"
                placeholder="e.g., 123456789012345"
                value={credentials.waba_id}
                onChange={(e) => setCredentials(prev => ({ ...prev, waba_id: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Found in your Meta app under WhatsApp → API Setup
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone_number_id">Phone Number ID</Label>
              <Input
                id="phone_number_id"
                placeholder="e.g., 987654321098765"
                value={credentials.phone_number_id}
                onChange={(e) => setCredentials(prev => ({ ...prev, phone_number_id: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                The ID of your WhatsApp Business phone number
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="access_token">Access Token</Label>
              <Input
                id="access_token"
                type="password"
                placeholder="Your permanent access token"
                value={credentials.access_token}
                onChange={(e) => setCredentials(prev => ({ ...prev, access_token: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Generate a permanent token in your app settings
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setStep('meta-setup')}>
                Back
              </Button>
              <Button onClick={handleConnect} disabled={isLoading} className="flex-1">
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    Connect WhatsApp <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Webhook configuration step (shown after successful connection)
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-8">
      <Card className="max-w-lg w-full">
        <CardHeader>
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <CheckCircle2 className="h-6 w-6 text-green-600" />
          </div>
          <CardTitle className="text-center">Step 3: Configure Webhook</CardTitle>
          <CardDescription className="text-center">
            Set up the webhook in Meta to receive incoming messages
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {webhookInfo && (
            <>
              <div className="space-y-2">
                <Label>Webhook URL</Label>
                <div className="flex gap-2">
                  <Input value={webhookInfo.url} readOnly className="font-mono text-sm" />
                  <Button
                    variant="outline"
                    onClick={() => navigator.clipboard.writeText(webhookInfo.url)}
                  >
                    Copy
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Verify Token</Label>
                <div className="flex gap-2">
                  <Input value={webhookInfo.verify_token} readOnly className="font-mono text-sm" />
                  <Button
                    variant="outline"
                    onClick={() => navigator.clipboard.writeText(webhookInfo.verify_token)}
                  >
                    Copy
                  </Button>
                </div>
              </div>
            </>
          )}

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Go to your Meta app → WhatsApp → Configuration → Webhook and paste these values.
              Subscribe to the "messages" webhook field.
            </AlertDescription>
          </Alert>

          <Button className="w-full" onClick={() => window.location.reload()}>
            Done - Start Messaging
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
